/* eslint-disable no-console */
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {Drawer, Flex, Input, Select, Tooltip} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import {ReactComponent as FacilityOverviewIcon} from '../../../../assets/icons/FacilityOverview.svg';
import MapComp from '../../../../components/MapComponents/MapComp';
import {getUniqueArray} from '../../../../shared/commonFunctions';
import {formatIndian} from '../../../../utils/formatIndian';

const ActivityPerformance: React.FC<{
  data: any;
}> = ({data}) => {
  const [continent, setContinent] = useState<string | undefined>('All');
  const [countryOptions, setCountryOptions] = useState<any[]>();
  const [mainData, setMainData] = useState(data?.facilitiesData ?? []);

  const [stateOptions, setStateOptions] = useState<any[]>();
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [state, setState] = useState<string | undefined>(undefined);
  const [cityMap, setCityMap] = useState<any[]>([]);
  const [highEmittingFacilities, setHighEmittingFacilities] = useState<any[]>(
    []
  );
  const mapRef: any[] = [];
  const [selectedFacility, setSelectedFacility] = useState<string | undefined>(
    undefined
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState('');
  const singleRef: any[] = [];

  useEffect(() => {
    setMainData(data?.facilitiesData ?? []);
  }, [data]);

  const facilityMean =
    mainData?.reduce(
      (acc: number, item: any) => acc + (item.emissions?.value ?? 0),
      0
    ) / mainData?.length;

  useEffect(() => {
    if (mainData.length > 0) {
      const stateFlat = mainData?.filter((item: any) => {
        return continent === 'All'
          ? country
            ? item?.country === country
            : true
          : item.continent === continent &&
              (!country || item?.country === country);
      });

      setCountryOptions(
        mainData?.map((item: any) => {
          return {
            value: item?.country,
            label: item?.country,
            continent: item.continent
          };
        })
      );

      setStateOptions(
        mainData?.map((item: any) => {
          return {
            value: item?.state,
            label: item?.state,
            country_id: item.country
          };
        })
      );

      setCityMap(
        stateFlat
          ?.filter((item: any) => {
            return state ? item?.state === state : true;
          })
          ?.map((item: any, index: number) => {
            return {...item, _id: index};
          })
      );
    }
  }, [continent, country, state, mainData]);

  useEffect(() => {
    const index = cityMap?.findIndex((t) => t?._id === selectedFacility);
    singleRef[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }, [selectedFacility]);

  useEffect(() => {
    setHighEmittingFacilities(
      mainData?.filter((item: any) => {
        return item.emissions.value > facilityMean;
      })
    );
  }, [mainData, facilityMean]);

  const continentOptions = useMemo(
    () =>
      getUniqueArray(mainData, 'continent')
        .map((item) => ({value: item.continent, label: item.continent}))
        .concat({label: 'All', value: 'All'})
        .reverse() ?? [{label: 'All', value: 'All'}],
    [mainData]
  );

  const drawerFacilityList = useMemo(() => {
    if (!drawerSearch?.trim()) return cityMap ?? [];
    const q = drawerSearch.trim().toLowerCase();

    return (cityMap ?? []).filter((item: any) =>
      (item?.name ?? '').toLowerCase().includes(q)
    );
  }, [cityMap, drawerSearch]);

  return (
    <>
      <Flex className="facilityOverviewTitle">Facility Overview</Flex>
      <Flex className="activityPerformance">
        <Flex className="contentFlex">
          <Flex className="mapFlex">
            <MapComp
              type="metricsMap"
              mapData={cityMap}
              markerRef={mapRef}
              setSelectedFacility={setSelectedFacility}
              facilityMean={facilityMean}
            />
          </Flex>
          <div className="facilityOverviewPanelWrapper">
            <Flex className="dataFlex">
              <Flex className="topFlex">
                <Flex
                  className="facilityNumber"
                  vertical={false}
                  justify="space-between"
                  align="center"
                >
                  <div className="fNFlex">
                    <div className="fCountBlock">
                      <div className="fNumber">{mainData.length}</div>
                      <div className="fText">Total Facilities</div>
                    </div>
                  </div>
                  <Flex className="facilityOverviewIcon">
                    <FacilityOverviewIcon />
                  </Flex>
                </Flex>
                <Flex className="cFlex" justify="space-around" align="center">
                  <div className="emissionSummaryHeader">
                    <Tooltip
                      title="We calculate the mean emissions of all listed facilities. Facilities above this mean
are categorized as high-emitting (red ↑), and those below as low-emitting (green ↓)."
                    >
                      <InfoCircleOutlined className="emissionSummaryInfoIcon" />
                    </Tooltip>
                  </div>
                  <div className="cNegativeFlex">
                    <div className="cPSymbol">
                      <ArrowUpOutlined className="emissionArrow highEmitting" />
                    </div>
                    <div className="cPInfo">
                      <div className="cNNumber">
                        {highEmittingFacilities.length}
                      </div>
                      <div className="cNText">High Emitting</div>
                    </div>
                  </div>
                  <div className="cPositiveFlex">
                    <div className="cPSymbol">
                      <ArrowDownOutlined className="emissionArrow lowEmitting" />
                    </div>
                    <div className="cPInfo">
                      <div className="cPNumber">
                        {mainData?.length - highEmittingFacilities?.length}
                      </div>
                      <div className="cPText">Low Emitting</div>
                    </div>
                  </div>
                </Flex>
              </Flex>
              <Flex className="listFlex">
                <Flex className="selectTabs">
                  <Select
                    className="selectContinents"
                    placeholder="Region"
                    options={continentOptions}
                    onChange={(e) => {
                      setContinent(e);
                      setCountry(undefined);
                      setState(undefined);
                    }}
                    value={continent}
                  />
                  <div>
                    <Select
                      allowClear
                      className="selectCountry"
                      placeholder="By Country"
                      options={
                        getUniqueArray(countryOptions ?? [], 'value')?.filter(
                          (item) => {
                            return continent === 'All'
                              ? true
                              : item.continent === continent;
                          }
                        ) ?? []
                      }
                      onChange={(e) => {
                        setCountry(e);
                        setState(undefined);
                      }}
                      value={country}
                    />
                    <Select
                      allowClear
                      className="selectStates"
                      placeholder="By State"
                      options={
                        getUniqueArray(stateOptions ?? [], 'value')?.filter(
                          (item) => {
                            return item.country_id === country;
                          }
                        ) ?? []
                      }
                      onChange={(e) => {
                        setState(e);
                      }}
                      value={state}
                    />
                  </div>
                </Flex>
              </Flex>
              <Flex className="countryList" vertical={true}>
                <div className="countryListScroll">
                  {(cityMap ?? []).map((item, index) => {
                    const {
                      name,
                      emissions: {value, unit}
                    } = item;
                    const isNegative = value > facilityMean;

                    return (
                      <Flex
                        ref={(ref: any) => singleRef.push(ref)}
                        className="countryDiv"
                        key={item._id ?? index}
                        justify="space-between"
                        align="center"
                        onClick={() => {
                          // const idx =
                          //   cityMap?.findIndex((c) => c._id === item._id) ??
                          //   index;
                          if (mapRef[index]) mapRef[index].fire('click');
                          setSelectedFacility(item._id);
                        }}
                        style={{
                          backgroundColor:
                            selectedFacility === item._id
                              ? '#DDF7FA39'
                              : '#FFFFFF',
                          borderLeft:
                            selectedFacility === item._id
                              ? '2px solid #3BB3C1'
                              : ''
                        }}
                      >
                        <div className="countryLabel">{name}</div>
                        <Flex gap={5} vertical={false} align="center">
                          {`${formatIndian(value)} ${unit}`}
                          {isNegative ? (
                            <ArrowUpOutlined className="emissionArrow highEmitting" />
                          ) : (
                            <ArrowDownOutlined className="emissionArrow lowEmitting" />
                          )}
                        </Flex>
                      </Flex>
                    );
                  })}
                </div>
                {cityMap && cityMap.length > 0 && (
                  <button
                    type="button"
                    className="seeAllFacilitiesLink"
                    onClick={() => {
                      setDrawerOpen(true);
                    }}
                  >
                    See all Facilities
                  </button>
                )}
              </Flex>
            </Flex>
            <Drawer
              title={null}
              placement="right"
              open={drawerOpen}
              closeIcon={false}
              onClose={() => {
                setDrawerOpen(false);
                setDrawerSearch('');
              }}
              getContainer={false}
              className="activityPerformanceDrawer"
              width="100%"
              styles={{
                body: {padding: '15px'},
                wrapper: {borderRadius: '0 16px 16px 0'}
              }}
              zIndex={20}
            >
              <Flex
                vertical={true}
                className="activityPerformanceDrawerContent"
                gap={15}
              >
                <Input
                  placeholder="Search Facility"
                  value={drawerSearch}
                  onChange={(e) => {
                    setDrawerSearch(e.target.value);
                  }}
                  className="drawerSearchInput"
                  suffix={<SearchOutlined className="drawerSearchIcon" />}
                  allowClear
                />
                <div className="drawerFacilityList">
                  {drawerFacilityList.map((item: any, index: number) => {
                    const {
                      name,
                      emissions: {value, unit}
                    } = item;
                    const isNegative = value > facilityMean;

                    return (
                      <Flex
                        key={item._id ?? index}
                        className="drawerFacilityItem"
                        justify="space-between"
                        align="center"
                        onClick={() => {
                          // const idx =
                          //   drawerFacilityList?.findIndex(
                          //     (c) => c._id === item._id
                          //   ) ?? index;
                          if (mapRef[index]) mapRef[index].fire('click');
                          setSelectedFacility(item._id);
                        }}
                      >
                        <div className="drawerFacilityName">{name}</div>
                        <Flex
                          gap={5}
                          vertical={false}
                          align="center"
                          className="drawerFacilityValue"
                        >
                          {`${formatIndian(value)} ${unit}`}
                          {isNegative ? (
                            <ArrowUpOutlined className="emissionArrow highEmitting" />
                          ) : (
                            <ArrowDownOutlined className="emissionArrow lowEmitting" />
                          )}
                        </Flex>
                      </Flex>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="drawerGoBackLink"
                  onClick={() => {
                    setDrawerOpen(false);
                    setDrawerSearch('');
                  }}
                >
                  Go back
                </button>
              </Flex>
            </Drawer>
          </div>
        </Flex>
      </Flex>
    </>
  );
};

export default ActivityPerformance;
